import { Head, Link, useForm, usePage } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import OrganizationFormFields from "@/pages/organizations/organization-form-fields";
import { manage_organizations_path, organization_path, organizations_path } from "@/routes";

function OrganizationForm({ organization = {} }) {
  const isEdit = Boolean(organization?.id);
  const {
    assignable_products: assignableProducts = [],
    selected_product_ids: selectedProductIds = []
  } = usePage().props;
  const { data, setData, post, patch, processing } = useForm({
    name: organization?.name || "",
    cvr: organization?.cvr || "",
    address: organization?.address || "",
    zip: organization?.zip || "",
    city: organization?.city || "",
    country_code: organization?.country_code || "DK",
    phone: organization?.phone || "",
    website: organization?.website || "",
    product_ids: organization?.product_ids || selectedProductIds
  });

  const toggleProduct = (productId) => {
    const selectedProductIds = data.product_ids.includes(productId)
      ? data.product_ids.filter((id) => id !== productId)
      : [...data.product_ids, productId];

    setData("product_ids", selectedProductIds);
  };

  const submit = (event) => {
    event.preventDefault();

    if (isEdit) {
      patch(organization_path(organization.id), { preserveScroll: true });
    } else {
      post(organizations_path(), { preserveScroll: true });
    }
  };

  return (
    <>
      <Head title={isEdit ? "Redigér organisation" : "Opret organisation"} />

      <section className="workspace-content">
        <div className="card">
          <div className="card-body">
            <div className="workspace-section__eyebrow">Administration</div>
            <h2 className="workspace-section__title">
              {isEdit ? "Redigér organisation" : "Opret organisation"}
            </h2>
            <p className="workspace-admin-copy">
              Administrér stamdata for organisationer og hold arbejdsrum og adgang samlet ét sted.
            </p>

            {organization?.errors?.length > 0 && (
              <div className="alert alert-danger mt-4">{organization.errors.join(", ")}</div>
            )}

            <form onSubmit={submit} className="vstack gap-4 mt-4">
              <OrganizationFormFields data={data} setData={setData} />

              <div className="workspace-admin-assignment">
                <div className="workspace-admin-assignment__header">
                  <div>
                    <h3 className="workspace-admin-assignment__title">Tilknyttede produkter</h3>
                    <p className="workspace-admin-assignment__copy">
                      Vælg hvilke produkter organisationen skal have adgang til i arbejdsrummet.
                    </p>
                  </div>
                  <span className="workspace-chip">{data.product_ids.length} valgt</span>
                </div>

                {assignableProducts.length === 0 ? (
                  <div className="workspace-admin-list__empty">
                    Der er ingen produkter at tilknytte endnu.
                  </div>
                ) : (
                  <div className="workspace-assignment-grid">
                    {assignableProducts.map((product) => {
                      const selected = data.product_ids.includes(product.id);

                      return (
                        <label
                          key={product.id}
                          className={`workspace-assignment-card ${selected ? "is-selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={selected}
                            onChange={() => toggleProduct(product.id)}
                          />
                          <div className="workspace-assignment-card__content">
                            <div className="workspace-assignment-card__title">{product.name}</div>
                            <div className="workspace-assignment-card__meta">
                              {product.steps_count} trin
                            </div>
                            <div className="workspace-assignment-card__description">
                              {product.description || "Ingen beskrivelse endnu."}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                <Link href={manage_organizations_path()} className="btn btn-outline-secondary">
                  Annuller
                </Link>
                <button type="submit" className="btn btn-primary" disabled={processing}>
                  {processing ? "Gemmer..." : isEdit ? "Gem ændringer" : "Opret organisation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

OrganizationForm.layout = (page) => {
  const { organization } = page.props;
  const isEdit = Boolean(organization?.id);

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Administration", href: manage_organizations_path() },
          { label: "Organisationer", href: manage_organizations_path() },
          { label: isEdit ? organization.name : "Opret" }
        ],
        title: isEdit ? "Redigér organisation" : "Opret organisation",
        actions: [
          {
            href: manage_organizations_path(),
            label: "Tilbage",
            variant: "outline-secondary"
          }
        ]
      }}
    >
      {page}
    </AuthLayout>
  );
};

export default OrganizationForm;
